import { Suspense, lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Loader } from '@sm-dashboard/ui-components';
import { myPageRoute, pagesList } from '@/constants';
import { ISRAEL_REGULATIONS_ROUTE, WORLD_REGULATIONS_ROUTE } from '@/util/regulationsRoute';
import { syncMissingRoutePrimaries, findMissingRoutes } from './routesSync';
import { PERSONAL_PAGE_FAMILY_LABELS, Route as RouteType } from '@sm-dashboard/types';
import RouteWrapper from './RouteWrapper';
import DashboardLayoutRoute from '@/components/routing/DashboardLayoutRoute';
import PublisherAdSubmissionPage from '@/pages/public/business-ad-submission/PublisherAdSubmissionPage';
import ShaliachAdSubmissionPage from '@/pages/dashboard/BusinessAd/ShaliachAdSubmissionPage';
import AdminBusinessAdsPage from '@/pages/dashboard/BusinessAd/admin/AdminBusinessAdsPage';
import BusinessAdPaymentSuccess from '@/pages/public/business-ad-submission/BusinessAdPaymentSuccess';
import BusinessAdPaymentError from '@/pages/public/business-ad-submission/BusinessAdPaymentError';
import Intro from '@/pages/auth/Intro';
import AppHomepage from '@/pages/auth/AppHomepage';
import AccountDeletion from '@/pages/public/AccountDeletion';

const Login = lazy(() => import('@/pages/auth/Login'));
const LoginWorld = lazy(() => import('@/pages/auth/LoginWorld'));
const RegisterWorld = lazy(() => import('@/pages/auth/RegisterWorld'));
const RegisterChooseType = lazy(() => import('@/pages/auth/RegisterChooseType'));
const RegisterForm = lazy(() => import('@/pages/auth/Register'));
const Archive = lazy(() => import('@/pages/dashboard/Archive'));
const EventsArchive = lazy(() => import('@/pages/dashboard/EventsArchive'));
const DonationsArchive = lazy(() => import('@/pages/dashboard/DonationsArchive'));
const PersonalDonationPage = lazy(() => import('@/pages/dashboard/PersonalDonationPage'));
const InformationPagesArchive = lazy(() => import('@/pages/dashboard/InformationPagesArchive'));
const LeadsPage = lazy(() => import('@/pages/dashboard/Leads'));
const Purchases = lazy(() => import('@/pages/dashboard/Purchases'));
const DonationsPage = lazy(() => import('@/pages/dashboard/Donations'));
const IncomesPage = lazy(() => import('@/pages/dashboard/Incomes'));
const IncomesDashboardPage = lazy(() => import('@/pages/dashboard/IncomesDashboard'));
const StandingOrdersPage = lazy(() => import('@/pages/dashboard/StandingOrders'));
const DonorsPage = lazy(() => import('@/pages/dashboard/Donors'));
const MyCentersPage = lazy(() => import('@/pages/dashboard/myCenters'));
const Kedma = lazy(() => import('@/pages/dashboard/Kedma'));
const Forms = lazy(() => import('@/pages/dashboard/Forms'));
const Shluchim = lazy(() => import('@/pages/dashboard/Shluchim'));
const SystemUsers = lazy(() => import('@/pages/dashboard/Shluchim').then(m => ({ default: m.SystemUsers })));
const ShluchimMailingHistory = lazy(() => import('@/pages/dashboard/ShluchimMailingHistory'));
const ShluchimGroupCompare = lazy(() => import('@/pages/dashboard/ShluchimGroupCompare'));
const Finance = lazy(() => import('@/pages/dashboard/Finance'));
const ConnectDepartmentEmailPage = lazy(() => import('@/pages/dashboard/mailing/ConnectDepartmentEmailPage'));
const Links = lazy(() => import('@/pages/dashboard/Links'));
const Feed = lazy(() => import('@/pages/dashboard/Feed'));
const PressWa = lazy(() => import('@/components/dashboard/PressWa'));
const ShluchimBook = lazy(() => import('@/pages/dashboard/ShluchimBook'));
const Details = lazy(() => import('@/pages/dashboard/Details'));
const Home = lazy(() => import('@/pages/dashboard/Home'));
const OrdersPage = lazy(() => import('@/pages/dashboard/Orders'));
const StamChecks = lazy(() => import('@/pages/dashboard/StamChecks'));
const Tags = lazy(() => import('@/pages/dashboard/Tags'));
const ArchiveGallery = lazy(() => import('@/pages/dashboard/ArchiveGallery'));
const FormResults = lazy(() => import('@/pages/dashboard/FormResults'));
const Branches = lazy(() => import('@/pages/dashboard/Branches'));
const BranchDetails = lazy(() => import('@/components/dashboard/Forms/Branch/BranchDetails'));
const PublicForm = lazy(() => import('@/pages/dashboard/PublicForm'));
const BusinessAds = lazy(() => import('@/pages/dashboard/BusinessAd/BusinessAds'));
const MyBusinessAdsPage = lazy(() => import('@/pages/dashboard/BusinessAd/MyBusinessAdsPage'));
const BusinessAdDetail = lazy(() => import('@/pages/dashboard/BusinessAd/BusinessAdDetail'));
const Favorites = lazy(() => import('@/pages/dashboard/Favorites'));
const CrmContactList = lazy(() => import('@/pages/dashboard/crm/CrmContactList'));
const CrmSetup = lazy(() => import('@/pages/dashboard/crm/CrmSetup'));
const CrmReconnect = lazy(() => import('@/pages/dashboard/crm/CrmReconnect'));
const CrmContactDetails = lazy(() => import('@/pages/dashboard/crm/CrmContactDetails'));
const CrmMailing = lazy(() => import('@/pages/dashboard/crm/CrmMailing'));
const CrmMailingHistory = lazy(() => import('@/pages/dashboard/crm/CrmMailingHistory'));
const CrmActivityHistory = lazy(() => import('@/pages/dashboard/crm/CrmActivityHistory'));
const CrmGroups = lazy(() => import('@/pages/dashboard/crm/CrmGroups'));
const CrmGroupActivityHistory = lazy(() => import('@/pages/dashboard/crm/CrmGroupActivityHistory'));
const CrmActivities = lazy(() => import('@/pages/dashboard/crm/CrmActivities'));
const CrmCommitments = lazy(() => import('@/pages/dashboard/crm/CrmCommitments'));
const CommitmentPaymentPage = lazy(() => import('@/pages/payment/CommitmentPaymentPage'));
const CrmContactFormPage = lazy(() => import('@/pages/public/CrmContactFormPage'));
const CrmActivityTopicTimeline = lazy(() => import('@/pages/dashboard/crm/CrmActivityTopicTimeline'));
const CrmPersonalBotPage = lazy(() => import('@/pages/dashboard/crm/CrmPersonalBot'));
const CrmRebbeReport = lazy(() => import('@/pages/dashboard/crm/CrmRebbeReport'));
const CrmDashboard = lazy(() => import('@/pages/dashboard/crm/CrmDashboard'));
const PaymentPage = lazy(() => import('@/pages/payment/PaymentPage'));
const PaymentSuccess = lazy(() => import('@/pages/payment/PaymentSuccess'));
const PaymentError = lazy(() => import('@/pages/payment/PaymentError'));
const PaymentsInstructions = lazy(() => import('@/pages/dashboard/PaymentsInstructions'));
const PersonalPages = lazy(() => import('@/pages/dashboard/PersonalPages'));
const HolidaysAndEnjoymentsPage = lazy(
  () => import('@/pages/dashboard/HolidaysAndEnjoyments/HolidaysAndEnjoymentsPage')
);
const SecurityForcesPage = lazy(() => import('@/pages/dashboard/SecurityForces/SecurityForcesPage'));
const SecurityForcesManagerPage = lazy(() => import('@/pages/dashboard/SecurityForces/SecurityForcesManagerPage'));
const SecurityForcesDashboardPage = lazy(() => import('@/pages/dashboard/SecurityForces/SecurityForcesDashboardPage'));
const MessengersNotSubmittedPage = lazy(() => import('@/pages/dashboard/SecurityForces/MessengersNotSubmittedPage'));
const DigitalScreenPage = lazy(() => import('@/pages/dashboard/DigitalScreen/DigitalScreenPage'));
const EducationGrantsPage = lazy(() => import('@/pages/dashboard/EducationGrants/EducationGrantsPage'));
const EducationGrantsManagerPage = lazy(() => import('@/pages/dashboard/EducationGrants/EducationGrantsManagerPage'));
const EducationGrantsDashboardPage = lazy(
  () => import('@/pages/dashboard/EducationGrants/EducationGrantsDashboardPage')
);
const EducationGrantsMessengersNotSubmittedPage = lazy(
  () => import('@/pages/dashboard/EducationGrants/MessengersNotSubmittedPage')
);
const EditOrderPage = lazy(() => import('@/pages/dashboard/HolidaysAndEnjoyments/EditOrderPage'));
const AdditionalDetailsPage = lazy(() => import('@/pages/dashboard/HolidaysAndEnjoyments/AdditionalDetailsPage'));
const HolidaysAndEnjoymentsLandingPage = lazy(() => import('@/pages/HolidaysAndEnjoymentsLandingPage'));
const FeedbackPage = lazy(() => import('@/pages/dashboard/HolidaysAndEnjoyments/FeedbackPage'));
const PersonalLinksPage = lazy(() => import('@/pages/dashboard/PersonalLinks'));
const PersonalCard = lazy(() => import('@/pages/dashboard/PersonalCard'));
const MyPage = lazy(() => import('@/pages/dashboard/MyWebsite/MyPage'));
const MyWebsitePage = lazy(() => import('@/pages/dashboard/MyWebsite/MyWebsitePage'));
const RegulationsPage = lazy(() => import('@/pages/dashboard/regulations'));
const WorldRegulationsPage = lazy(() => import('@/pages/dashboard/world-regulations'));
const SubscriptionPlans = lazy(() => import('@/pages/SubscriptionPlans'));
const RegisterationsPage = lazy(() => import('@/pages/dashboard/Registerations'));
const AllPersonalTicketsPage = lazy(() => import('@/pages/dashboard/AllPersonalTicketsPage'));
const EventSalesPage = lazy(() => import('@/pages/dashboard/EventSales'));
const EventCreationContent = lazy(
  () => import('@/components/dashboard/PersonalPage/PersonalEvent/EventCreationContent')
);
const CampaignCreationContent = lazy(
  () => import('@/components/dashboard/PersonalPage/PersonalDonations/DonationCreationContent')
);
const InformationCreationContent = lazy(
  () => import('@/components/dashboard/PersonalPage/PersonalInformation/InformationCreationContent')
);
const TicketSaleTestPage = lazy(() => import('@/pages/test/TicketSaleTestPage'));
const ShaliachRegistration = lazy(() => import('@/pages/public/ShaliachRegistration'));
const CandidateRegistration = lazy(() => import('@/pages/public/CandidateRegistration'));
const ShaliachPaymentTestPage = lazy(() => import('@/pages/payment/ShaliachPaymentTestPage'));
const PrivacyPolicy = lazy(() => import('@/pages/auth/PrivacyPolicy'));
const DailyGiveOperatorPrivacyPolicy = lazy(() =>
  import('@/pages/public/legal/daily-give/DailyGivePrivacyPolicy').then(m => ({
    default: m.DailyGiveOperatorPrivacyPolicy
  }))
);
const DailyGiveApplePrivacyPolicy = lazy(() =>
  import('@/pages/public/legal/daily-give/DailyGivePrivacyPolicy').then(m => ({
    default: m.DailyGiveApplePrivacyPolicy
  }))
);
const TicketDisplayPage = lazy(() => import('@/pages/public/TicketDisplayPage'));
const NewPage = lazy(() => import('@/pages/dashboard/NewPage'));
const PublicMyopPageShell = lazy(() => import('@/pages/public/PublicMyopPageShell'));
const SystemMembersPage = lazy(() =>
  import('@/pages/dashboard/SystemMembers/SystemMembersList').then(m => ({ default: m.default }))
);
const SystemMembersEditPage = lazy(() =>
  import('@/pages/dashboard/SystemMembers/SystemMembersEdit').then(m => ({ default: m.default }))
);
const ShaliachActivities = lazy(() => import('@/pages/dashboard/Activities'));
const SubscriptionPlansManage = lazy(() => import('@/pages/manage/SubscriptionPlansManage'));
const SubscriptionsManage = lazy(() => import('@/pages/manage/SubscriptionsManage'));
const SubscriptionsDashboard = lazy(() => import('@/pages/manage/SubscriptionsDashboard'));
const VenuesManage = lazy(() => import('@/pages/manage/VenuesManage'));
const ShabbatMealsManage = lazy(() => import('@/pages/manage/ShabbatMealsManage'));
const ShabbatMealsOrderPage = lazy(() => import('@/pages/public/ShabbatMealsOrderPage'));
const ShabbatMealsSeriesPage = lazy(() => import('@/pages/public/ShabbatMealsSeriesPage'));
const ShabbatMealsWizard = lazy(() => import('@/pages/manage/ShabbatMealsWizard'));
const CrmSmsUsagePage = lazy(() => import('@/pages/dashboard/CrmSmsUsagePage'));
const KedmaAiUsagePage = lazy(() => import('@/pages/dashboard/KedmaAiUsagePage'));
const Webhooks = lazy(() => import('@/pages/dashboard/Webhooks'));
const DonationRefundRequestsGrid = lazy(
  () => import('@/components/dashboard/grid/donationRefundRequests/DonationRefundRequestsGrid')
);

const routesList: RouteType[] = [
  { route: '/', element: <Intro />, primary: 'נתיב ראשי לדף הבית', isPublic: true },
  { route: '/intro', element: <Intro />, primary: 'כניסה', isPublic: true },
  { route: '/login', element: <Login />, primary: 'התחברות', isPublic: true },
  {
    route: '/shabbat-meals/series/:seriesId',
    element: <ShabbatMealsSeriesPage />,
    primary: 'הרשמה לסעודות שבת',
    isPublic: true
  },
  {
    route: '/shabbat-meals/order/:instanceId',
    element: <ShabbatMealsOrderPage />,
    primary: 'הרשמה לסעודות שבת',
    isPublic: true
  },
  { route: '/login-world', element: <LoginWorld />, primary: 'התחברות בתי חב״ד בעולם', isPublic: true },
  { route: '/register/choose', element: <RegisterChooseType />, primary: 'בחירת סוג הרשמה', isPublic: true },
  { route: '/register', element: <RegisterForm />, primary: 'הרשמה', isPublic: true },
  { route: '/register-world', element: <RegisterWorld />, primary: 'הרשמה בתי חב״ד בעולם', isPublic: true },
  { route: '/shaliach-registration', element: <ShaliachRegistration />, primary: 'רישום שליח ציבורי', isPublic: true },
  {
    route: '/candidate-registration',
    element: <CandidateRegistration />,
    primary: 'טופס מועמד לשליחות',
    isPublic: true
  },
  { route: '/desk/leads', element: <LeadsPage />, primary: 'פניות' },
  { route: '/desk/donations', element: <DonationsPage />, primary: 'תרומות' },
  { route: '/desk/donations/donors', element: <DonorsPage />, primary: 'תורמים' },
  { route: '/desk/incomes', element: <IncomesPage />, primary: 'הכנסות' },
  { route: '/desk/incomes-dashboard', element: <IncomesDashboardPage />, primary: 'מבט על' },
  { route: '/desk/standing-orders', element: <StandingOrdersPage />, primary: 'הוראות קבע' },
  { route: '/outreach/orders', element: <OrdersPage />, primary: 'העיצובים שהזמנתי' },
  { route: '/desk/purchases', element: <Purchases />, primary: 'מכירות' },
  { route: '/outreach/kedma', element: <Kedma />, primary: 'קדמה - עיצובים' },
  { route: '/outreach/press-wa', element: <PressWa />, primary: 'פרסומים להפצה' },
  { route: '/org/archive', element: <Archive />, primary: 'צא״ח דרייב' },
  { route: '/outreach/events-archive', element: <EventsArchive />, primary: 'ארכיון אירועים' },
  { route: '/outreach/donations-archive', element: <DonationsArchive />, primary: 'ארכיון תרומות' },
  {
    route: '/outreach/personal-donation',
    element: <PersonalDonationPage />,
    primary: `יצירת דף התרמה כללי`
  },
  { route: '/outreach/information-pages-archive', element: <InformationPagesArchive />, primary: 'ארכיון דפי מידע' },
  {
    route: '/outreach/event-creation',
    element: <EventCreationContent />,
    primary: `יצירת ${PERSONAL_PAGE_FAMILY_LABELS.event.singular}`
  },
  {
    route: '/outreach/campaign-creation',
    element: <CampaignCreationContent />,
    primary: `יצירת ${PERSONAL_PAGE_FAMILY_LABELS.campaign.singular}`
  },
  {
    route: '/outreach/information-page-creation',
    element: <InformationCreationContent />,
    primary: `יצירת ${PERSONAL_PAGE_FAMILY_LABELS.information.singular}`
  },
  {
    route: '/outreach/registration-creation',
    element: <InformationCreationContent />,
    primary: `יצירת ${PERSONAL_PAGE_FAMILY_LABELS.information.singular}`
  },
  { route: '/org/forms', element: <Forms />, primary: 'טפסים ושאלונים' },
  { route: '/org/forms/mod/:formId', element: <Forms />, primary: 'מילוי טופס' },
  { route: '/org/forms/results/:formId', element: <FormResults />, primary: 'טבלת תוצאות טפסים' },
  { route: '/forms', element: <Forms />, primary: 'טפסים ושאלונים' },
  { route: '/system/users', element: <SystemUsers />, primary: 'ניהול משתמשים' },
  { route: '/system/shluchim', element: <Shluchim />, primary: 'ניהול שלוחים' },
  {
    route: '/system/shluchim/mailing-history',
    element: <ShluchimMailingHistory />,
    primary: 'היסטוריית דיוור - שלוחים'
  },
  {
    route: '/system/shluchim/group-compare',
    element: <ShluchimGroupCompare />,
    primary: 'השוואת קבוצה לקובץ'
  },
  { route: '/system/shluchim/profile', element: <Details />, primary: 'הפרטים שלי' },
  { route: '/system/shluchim/:id', element: <Details />, primary: 'פרטי שליח וסניף' },
  { route: '/system/shaliach-activities', element: <ShaliachActivities />, primary: 'לוג פעילות שלוחים' },
  { route: '/org/finance', element: <Finance />, primary: 'זיכוים חב"ד ישראל' },
  {
    route: '/org/donation-refund-requests',
    element: <DonationRefundRequestsGrid />,
    primary: 'בקשות ביצוע תשלום'
  },
  { route: '/system/mailing/connect-email', element: <ConnectDepartmentEmailPage />, primary: 'חיבור מייל לאגפים' },
  { route: '/org/shluchim-book', element: <ShluchimBook />, primary: 'ספר השלוחים' },
  { route: '/org/links', element: <Links />, primary: 'קישורים שימושיים' },
  { route: '/org/feed/:postId', element: <Feed />, primary: 'חדשות - גלילה לפוסט מסוים' },
  { route: '/org/feed', element: <Feed />, primary: 'חדשות' },
  { route: '/system/admin/business-ads', element: <AdminBusinessAdsPage />, primary: 'אדמין לוח' },
  {
    route: '/business-ads/submit/publisher',
    element: <PublisherAdSubmissionPage />,
    primary: 'פרסום מודעה חדשה',
    isPublic: true
  },
  {
    route: '/tools/business-ads/submit/shaliach',
    element: <ShaliachAdSubmissionPage />,
    primary: 'פרסום מודעה לשליחים'
  },
  {
    route: '/business-ads/payment/success/:adId',
    element: <BusinessAdPaymentSuccess />,
    primary: 'תשלום הושלם בהצלחה',
    isPublic: true
  },
  {
    route: '/business-ads/payment/error/:adId',
    element: <BusinessAdPaymentError />,
    primary: 'שגיאה בתשלום',
    isPublic: true
  },
  { route: '/tools/business-ads/mine', element: <MyBusinessAdsPage />, primary: 'הלוח - המודעות שלי' },
  { route: '/tools/business-ads/:adId', element: <BusinessAdDetail />, primary: 'לוח - פרטי מודעה' },
  { route: '/tools/business-ads', element: <BusinessAds />, primary: 'הלוח -ראשי' },
  { route: '/tools/favorites', element: <Favorites />, primary: 'הלוח - מועדפים' },
  { route: '/org/home', element: <Home />, primary: 'דף הבית' },
  { route: '/org/new-page', element: <NewPage />, primary: 'דף חדש' },
  { route: '/tools/stam-checks/system', element: <StamChecks />, primary: 'מערכת בדיקות סת"ם' },
  { route: '/system/tags', element: <Tags />, primary: 'ניהול תגיות' },
  { route: '/org/archive-gallery', element: <ArchiveGallery />, primary: 'חב"ד בפריים' },
  { route: '/system/branches', element: <Branches />, primary: 'ניהול סניפים' },
  { route: '/system/branches/:id', element: <BranchDetails />, primary: 'ניהול סניפים - פרטי סניף' },
  { route: '/public-form/:formId', element: <PublicForm />, primary: 'טופס ציבורי - לפי מזהה', isPublic: true },
  { route: '/public-form', element: <PublicForm />, primary: 'טופס ציבורי', isPublic: true },
  { route: '/crm/setup', element: <CrmSetup />, primary: 'התחברות' },
  { route: '/crm/reconnect', element: <CrmReconnect />, primary: 'חיבור CRM' },
  { route: '/crm/dashboard', element: <CrmDashboard />, primary: 'לוח בקרה CRM' },
  { route: '/crm/contacts', element: <CrmContactList />, primary: 'ניהול אנשי קשר - CRM' },
  { route: '/crm/contact/:id', element: <CrmContactDetails />, primary: 'CRM - פרטי איש קשר' },
  { route: '/crm/contact/:id/edit', element: <CrmContactDetails />, primary: 'CRM - עריכת איש קשר' },
  { route: '/crm/contact/:id/activity', element: <CrmActivityHistory />, primary: 'CRM - היסטוריית פעילות' },
  { route: '/crm/groups', element: <CrmGroups />, primary: 'קבוצות' },
  {
    route: '/crm/groups/:groupType/:groupValue/activity',
    element: <CrmGroupActivityHistory />,
    primary: 'CRM - היסטוריית פעילות לקבוצה'
  },
  { route: '/crm/activities', element: <CrmActivities />, primary: 'אני פעילויות' },
  {
    route: '/crm/activities/:topicKey/timeline',
    element: <CrmActivityTopicTimeline />,
    primary: 'CRM - ציר פעילות לפי נושא'
  },
  { route: '/crm/mailing-history', element: <CrmMailingHistory />, primary: 'CRM - היסטוריית דיוור' },
  { route: '/crm/mailing', element: <CrmMailing />, primary: 'CRM - דיוור' },
  { route: '/crm/commitments', element: <CrmCommitments />, primary: 'CRM - התחייבויות' },
  { route: '/crm/whatsapp-bot', element: <CrmPersonalBotPage />, primary: 'CRM - בוט ווטצאפ' },
  { route: '/crm/rebbe-report', element: <CrmRebbeReport />, primary: 'דוח לרבי' },
  { route: '/crm/forms/user-forms', element: <Forms userFormsOnly={true} />, primary: 'CRM - הטפסים ושאלונים' },
  { route: '/payment', element: <PaymentPage />, primary: 'תשלום', isPublic: true },
  {
    route: '/pay/:token',
    element: <CommitmentPaymentPage />,
    primary: 'תשלום התחייבות',
    isPublic: true
  },
  {
    route: '/contact-form/:token',
    element: <CrmContactFormPage />,
    primary: 'טופס עדכון פרטים',
    isPublic: true
  },
  { route: '/payment/success', element: <PaymentSuccess />, primary: 'הצלחה בתשלום', isPublic: true },
  { route: '/payment/error', element: <PaymentError />, primary: 'שגיאת תשלום', isPublic: true },
  { route: '/system/payments', element: <PaymentsInstructions />, primary: 'ניהול תשלומים' },
  { route: '/outreach/personal-pages', element: <PersonalPages />, primary: 'דפים אישיים' },
  { route: '/outreach/personal-links', element: <PersonalLinksPage />, primary: 'קישורים אישיים' },
  { route: '/desk/registerations', element: <RegisterationsPage />, primary: 'הרשמות וכרטיסים' },
  {
    route: '/desk/registerations/campaigns',
    element: <RegisterationsPage defaultPageType="campaign" />,
    primary: 'קמפיינים'
  },
  {
    route: '/desk/registerations/all-tickets',
    element: <AllPersonalTicketsPage />,
    primary: 'כל הכרטיסים מאירועים'
  },
  { route: '/org/event-sales/:eventId', element: <EventSalesPage />, primary: 'טבלת מכירות' },
  { route: '/outreach/my-centers', element: <MyCentersPage />, primary: 'המוקדים שלי' },
  { route: '/tools/holidays-and-enjoyments', element: <HolidaysAndEnjoymentsPage />, primary: 'מבצע חינוך - ראשי' },
  { route: '/tools/holidays-and-enjoyments/edit/:id', element: <EditOrderPage />, primary: 'מבצע חינוך - עריכת הזמנה' },
  { route: '/tools/security-forces', element: <SecurityForcesPage />, primary: 'כוחות הביטחון' },
  {
    route: '/system/security-forces/system',
    element: <SecurityForcesManagerPage />,
    primary: 'כוחות הביטחון - מערכת מנהלים'
  },
  {
    route: '/tools/security-forces/dashboard',
    element: <SecurityForcesDashboardPage />,
    primary: 'כוחות הביטחון - לוח בקרה'
  },
  {
    route: '/tools/security-forces/messengers-not-submitted',
    element: <MessengersNotSubmittedPage />,
    primary: 'כוחות הביטחון - שליחים שלא הגישו'
  },
  { route: '/tools/education-grants', element: <EducationGrantsPage />, primary: 'מבצע חינוך מענקים' },
  { route: '/tools/digital-screen', element: <DigitalScreenPage />, primary: 'מסך פרסום דיגיטלי' },
  {
    route: '/system/education-grants/system',
    element: <EducationGrantsManagerPage />,
    primary: 'מבצע חינוך מענקים - מערכת מנהלים'
  },
  {
    route: '/system/education-grants/dashboard',
    element: <EducationGrantsDashboardPage />,
    primary: 'מבצע חינוך מענקים - לוח בקרה'
  },
  {
    route: '/system/education-grants/messengers-not-submitted',
    element: <EducationGrantsMessengersNotSubmittedPage />,
    primary: 'מבצע חינוך מענקים - שליחים שלא הגישו'
  },
  {
    route: '/holidays-and-enjoyments-landing/:orderId',
    element: <HolidaysAndEnjoymentsLandingPage />,
    primary: 'דף נחיתה מבצע חינוך',
    isPublic: true
  },
  {
    route: '/tools/holidays-and-enjoyments/system',
    element: <HolidaysAndEnjoymentsPage />,
    primary: 'מבצע חינוך - מערכת הזמנות מוסדות'
  },
  {
    route: '/holidays-and-enjoyments/additional-details/:orderId',
    element: <AdditionalDetailsPage />,
    primary: 'מבצע חינוך - פרטי הזמנה',
    isPublic: true
  },
  {
    route: '/holidays-and-enjoyments/feedback/:orderId',
    element: <FeedbackPage />,
    primary: 'מבצע חינוך - משוב',
    isPublic: true
  },
  {
    route: '/holidays-and-enjoyments-landing/feedback/:orderId',
    element: <FeedbackPage />,
    primary: 'מבצע חינוך - משוב (דף נחיתה)',
    isPublic: true
  },
  { route: '/outreach/personal-card/:id', element: <PersonalCard />, primary: 'כרטיס ביקור - לפי שליח' },
  { route: '/outreach/personal-card', element: <PersonalCard />, primary: 'כרטיס ביקור' },
  { route: myPageRoute, element: <MyPage />, primary: 'הדף שלי' },
  { route: '/outreach/my-website', element: <MyWebsitePage />, primary: 'האתר שלי' },
  { route: ISRAEL_REGULATIONS_ROUTE, element: <RegulationsPage />, primary: 'תקנונים ומדריכים — ישראל' },
  { route: WORLD_REGULATIONS_ROUTE, element: <WorldRegulationsPage />, primary: 'תקנונים ומדריכים — חו״ל' },
  { route: '/org/subscription-plans', element: <SubscriptionPlans />, primary: 'המנוי שלי' },
  { route: '/system/subscription-plans', element: <SubscriptionPlansManage />, primary: 'תכניות מנוי (מנוע)' },
  { route: '/system/subscriptions', element: <SubscriptionsManage />, primary: 'מנויים' },
  { route: '/system/subscriptions-dashboard', element: <SubscriptionsDashboard />, primary: 'דשבורד מנויים' },
  { route: '/system/venues', element: <VenuesManage />, primary: 'מקומות אירוע' },
  { route: '/org/shabbat-meals', element: <ShabbatMealsManage />, primary: 'סעודות שבת' },
  { route: '/org/shabbat-meals/wizard/:seriesId', element: <ShabbatMealsWizard />, primary: 'עריכת אירוע סעודות' },
  { route: '/system/members', element: <SystemMembersPage />, primary: 'חברי מערכת' },
  { route: '/system/members/:id', element: <SystemMembersEditPage />, primary: 'חברי מערכת - עריכה' },
  { route: '/system/crm-sms-usage', element: <CrmSmsUsagePage />, primary: 'דוח שימוש SMS - CRM' },
  { route: '/system/ai-usage', element: <KedmaAiUsagePage />, primary: 'דוח שימוש AI - קדמה' },
  { route: '/system/webhooks', element: <Webhooks />, primary: 'וובהוקים' },
  {
    route: '/system/donation-refund-requests',
    element: <Navigate to="/org/donation-refund-requests" replace />,
    primary: 'בקשות ביצוע תשלום'
  },

  {
    route: '/event/order/:eventId',
    element: <PublicMyopPageShell pageType="event" />,
    primary: 'הזמנת כרטיסים',
    isPublic: true
  },
  { route: '/ticket/display/:ticketToken', element: <TicketDisplayPage />, primary: 'כרטיס', isPublic: true },
  {
    route: '/campaign-donation/:campaignId',
    element: <PublicMyopPageShell pageType="campaign" />,
    primary: 'תרומה לקמפיין',
    isPublic: true
  },
  {
    route: '/info-page/:pageId',
    element: <PublicMyopPageShell pageType="information" />,
    primary: 'דף מידע',
    isPublic: true
  },

  { route: '/test/ticket-sale', element: <TicketSaleTestPage />, primary: 'בדיקת מכירת כרטיסים' },
  { route: '/test/payment-shaliach/:shaliachId', element: <ShaliachPaymentTestPage />, primary: 'בדיקת תשלום שליח' },
  {
    route: '/test/payment-shaliach/:shaliachId/success',
    element: <ShaliachPaymentTestPage />,
    primary: 'בדיקת תשלום שליח - הצלחה'
  },
  {
    route: '/test/payment-shaliach/:shaliachId/failed',
    element: <ShaliachPaymentTestPage />,
    primary: 'בדיקת תשלום שליח - כשל'
  },
  { route: '/privacy-policy', element: <PrivacyPolicy />, primary: 'מדיניות הפרטיות', isPublic: true },
  {
    route: '/privacy-policy/daily-give',
    element: <DailyGiveOperatorPrivacyPolicy />,
    primary: 'Daily Give Privacy Policy',
    isPublic: true
  },
  {
    route: '/privacy-policy/daily-give/apple',
    element: <DailyGiveApplePrivacyPolicy />,
    primary: 'Daily Give Privacy Policy (Apple)',
    isPublic: true
  },
  { route: '/delete-account', element: <AccountDeletion />, primary: 'מחיקת חשבון', isPublic: true },
  { route: '/apphome', element: <AppHomepage />, primary: 'דף הבית', isPublic: true }
];

export const routes = syncMissingRoutePrimaries(routesList, pagesList);

if (process.env.NODE_ENV === 'development') {
  findMissingRoutes(routesList, pagesList);
}

const publicRoutes = routes.filter(route => route.isPublic);
const dashboardRoutes = routes.filter(route => !route.isPublic);

const App = () => {
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: 0,
            left: 0
          }}
        >
          <Loader />
        </Box>
      }
    >
      <Routes>
        {publicRoutes.map(route => (
          <Route key={route.route} path={route.route} element={<RouteWrapper route={route} />} />
        ))}

        <Route path="/*" element={<DashboardLayoutRoute />}>
          {dashboardRoutes.map(route => {
            const nestedPath =
              route.route === '/' ? '' : route.route.startsWith('/') ? route.route.slice(1) : route.route;
            return <Route key={route.route} path={nestedPath} element={route.element} />;
          })}
        </Route>
      </Routes>
    </Suspense>
  );
};

export default App;
